import { useCallback, useEffect, useRef, useState } from "react";

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes of inactivity before warning
const WARNING_SECONDS = 30; // countdown shown before auto logout

export function useIdleLogout({ active, onLogout }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);

  const idleTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const showWarningRef = useRef(false);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const startCountdown = useCallback(() => {
    setShowWarning(true);
    showWarningRef.current = true;
    setSecondsLeft(WARNING_SECONDS);
    countdownRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          showWarningRef.current = false;
          setShowWarning(false);
          onLogoutRef.current?.();
          return WARNING_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(startCountdown, IDLE_TIMEOUT_MS);
  }, [startCountdown]);

  const handleActivity = useCallback(() => {
    if (showWarningRef.current) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      showWarningRef.current = false;
      setShowWarning(false);
    }
    resetIdleTimer();
  }, [resetIdleTimer]);

  useEffect(() => {
    if (!active) {
      clearTimers();
      showWarningRef.current = false;
      setShowWarning(false);
      return;
    }

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));
    resetIdleTimer();

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
      clearTimers();
    };
  }, [active, handleActivity, resetIdleTimer, clearTimers]);

  const stayLoggedIn = useCallback(() => {
    handleActivity();
  }, [handleActivity]);

  return { showWarning, secondsLeft, stayLoggedIn };
}